"use client";

import {
  Conflicts,
  getAvailableTimeSlotsForDate,
  getDisabledDatesInRange,
  getServiceBookingWindow,
} from "@/lib/utils/booking";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useFormatter, useTranslations } from "next-intl";
import { getServiceAvailability } from "@/app/actions";
import { useEffect, useMemo, useState } from "react";
import { Creator, Service } from "@/lib/entities";
import { TimeSelector } from "./time-selector";
import { Heading } from "./catalyst/heading";
import { useRouter } from "next/navigation";
import { Button } from "./catalyst/button";
import { DatePicker } from "./date-picker";

interface ServiceCheckoutCarouselProps {
  service: Service;
  creator: Creator;
  userId?: string;
  className?: string;
}

type ViewMode = "date" | "time";

export function ServiceCheckoutCarousel({
  service,
  creator,
  userId,
  className,
}: ServiceCheckoutCarouselProps) {
  const t = useTranslations("ServiceCheckoutCarousel");
  const format = useFormatter();
  const router = useRouter();
  const [conflictsByMonth, setConflictsByMonth] = useState<
    Record<string, Conflicts>
  >({});
  const [loadingAvailability, setLoadingAvailability] = useState<boolean>(true);

  const { minDate, maxDate } = useMemo(
    () => getServiceBookingWindow(service),
    [service],
  );

  const initialMonthView = new Date(
    minDate.getFullYear(),
    minDate.getMonth(),
    1,
  );
  const [currentMonthView, setCurrentMonthView] = useState(initialMonthView);

  const [currentDate, setCurrentDate] = useState<Date>(minDate);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("date");

  const monthKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  // Compute disabled dates only for the visible month to keep the work
  // proportional to what the user sees.
  const disabledDates = useMemo(() => {
    const monthStart = new Date(
      currentMonthView.getFullYear(),
      currentMonthView.getMonth(),
      1,
    );
    const monthEnd = new Date(
      currentMonthView.getFullYear(),
      currentMonthView.getMonth() + 1,
      0,
    );
    const from = monthStart < minDate ? minDate : monthStart;
    const to = monthEnd > maxDate ? maxDate : monthEnd;
    const conflicts = conflictsByMonth[monthKey(currentMonthView)] ?? [];
    return getDisabledDatesInRange(service, from, to, conflicts);
  }, [service, currentMonthView, minDate, maxDate, conflictsByMonth]);

  useEffect(() => {
    const key = monthKey(currentMonthView);
    if (conflictsByMonth[key]) return;

    const fetchConflicts = async () => {
      try {
        setLoadingAvailability(true);

        const data = await getServiceAvailability(service.slug, key, userId);

        setConflictsByMonth((prev) => ({
          ...prev,
          [key]: data.conflicts || [],
        }));
      } catch {
        // noop
      } finally {
        setLoadingAvailability(false);
      }
    };
    fetchConflicts();
  }, [currentMonthView, service.slug, conflictsByMonth, userId]);

  const availableTimeSlots = useMemo(() => {
    const key = monthKey(currentDate);
    return getAvailableTimeSlotsForDate(
      service,
      currentDate,
      conflictsByMonth[key],
    );
  }, [service, currentDate, conflictsByMonth]);

  const handleDateSelect = (date: Date) => {
    setCurrentDate(date);
    setCurrentMonthView(new Date(date.getFullYear(), date.getMonth(), 1));
    setViewMode("time");
    setSelectedDateTime(null);
  };

  const handleTimeSelect = (datetime: Date) => {
    setSelectedDateTime(datetime);
  };

  const handleBackToDate = () => {
    setViewMode("date");
    setSelectedDateTime(null);
  };

  const handlePrevMonth = () => {
    setCurrentMonthView(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonthView(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const handleContinueToCheckout = () => {
    if (!selectedDateTime) return;
    const checkoutUrl = `/p/${creator.slug}/services/${service.slug}/checkout?datetime=${selectedDateTime.toISOString()}`;
    router.push(checkoutUrl);
  };

  const timezone = service.timezone;
  const monthLabel = format.dateTime(currentMonthView, "monthYear", {
    timeZone: timezone,
  });

  return (
    <div className={className}>
      <div className="p-6">
        {viewMode === "date" && (
          <div>
            <Header title={t("chooseDate")} timezone={timezone} />

            <MonthNavigator
              label={monthLabel}
              current={currentMonthView}
              min={minDate}
              max={maxDate}
              onPrev={handlePrevMonth}
              onNext={handleNextMonth}
            />

            <div className="relative" data-testid="date-picker">
              <DatePicker
                value={currentDate}
                activeStartDate={currentMonthView}
                minDate={minDate}
                maxDate={maxDate}
                disabledDates={disabledDates}
                onMonthViewChange={setCurrentMonthView}
                onSelect={handleDateSelect}
              />
              {loadingAvailability && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-zinc-900/40"
                  role="status"
                  aria-live="polite"
                >
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-purple-600 dark:border-zinc-700 dark:border-t-purple-400" />
                  <span className="sr-only">{t("loadingCalendar")}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === "time" && !!availableTimeSlots.length && (
          <div>
            <Header title={t("chooseTime")} timezone={timezone} />

            <TimeNavigator
              date={currentDate}
              durationMin={service.durationMin}
              timezone={timezone}
              onBack={handleBackToDate}
            />

            <TimeSelector
              selectedTime={selectedDateTime}
              availableTimeSlots={availableTimeSlots}
              timezone={timezone}
              onSelect={handleTimeSelect}
            />

            <div className="mt-6 flex flex-col">
              <Button
                onClick={handleContinueToCheckout}
                color="purple"
                disabled={!selectedDateTime}
                data-testid="continue-to-checkout-button"
              >
                {t("continueToCheckout")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Header({
  title,
  timezone,
}: Readonly<{ title: string; timezone: string; className?: string }>) {
  return (
    <div className="mb-6 text-center">
      <Heading level={2}>{title}</Heading>
      {timezone && (
        <div className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          {timezone}
        </div>
      )}
    </div>
  );
}

function NavButton({
  onClick,
  disabled,
  ariaLabel,
  children,
  testId,
}: Readonly<{
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
  children: React.ReactNode;
  testId?: string;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      className={`inline-flex items-center justify-center rounded-full p-2 ${
        disabled
          ? "text-zinc-400 cursor-not-allowed"
          : "text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-400/10 cursor-pointer"
      }`}
    >
      {children}
    </button>
  );
}

function MonthNavigator({
  label,
  current,
  min,
  max,
  onPrev,
  onNext,
}: Readonly<{
  label: string;
  current: Date;
  min?: Date;
  max?: Date;
  onPrev: () => void;
  onNext: () => void;
}>) {
  const t = useTranslations("ServiceCheckoutCarousel");
  const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
  const prevMonthEnd = new Date(monthStart);
  prevMonthEnd.setDate(0);
  const nextMonthStart = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    1,
  );
  const minD = min ? new Date(min) : undefined;
  const maxD = max ? new Date(max) : undefined;
  const canPrev = minD ? prevMonthEnd >= minD : true;
  const canNext = maxD ? nextMonthStart <= maxD : true;

  return (
    <div className="mb-4 flex items-center justify-between">
      <NavButton
        onClick={onPrev}
        disabled={!canPrev}
        ariaLabel={t("previousMonth")}
        testId="calendar-prev-month"
      >
        <ChevronLeftIcon className="size-6" />
      </NavButton>

      <div className="rounded-full bg-purple-600 px-5 py-2 text-sm font-semibold text-white">
        {label}
      </div>

      <NavButton
        onClick={onNext}
        disabled={!canNext}
        ariaLabel={t("nextMonth")}
        testId="calendar-next-month"
      >
        <ChevronRightIcon className="size-6" />
      </NavButton>
    </div>
  );
}

function TimeNavigator({
  date,
  durationMin,
  timezone,
  onBack,
}: Readonly<{
  date: Date;
  durationMin?: number;
  timezone?: string;
  onBack: () => void;
}>) {
  const t = useTranslations("ServiceCheckoutCarousel");
  const format = useFormatter();
  const formattedDate = format.dateTime(date, {
    timeZone: timezone,
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="mb-6 flex items-center justify-between">
      <NavButton
        onClick={onBack}
        disabled={false}
        ariaLabel={t("backToDateSelection")}
      >
        <ChevronLeftIcon className="size-6" />
      </NavButton>
      <div className="text-right">
        <div className="text-sm italic">
          {formattedDate}
          {durationMin && ` (${t("min", { durationMin })})`}
        </div>
      </div>
    </div>
  );
}
